import { ChallengeBundleParam } from 'src/pages/constants';
import { Plan } from 'src/pages/deprecated/payment/types';
import { ChallengeBundleParamId, ChallengePlan } from 'src/pages/types';
import { ChallengeAssetsMetadata } from 'src/store/signup-workflow/types';
import { getPlanPricing, formatPricingAmount } from './plans.util';
import { getCurrencySymbol } from 'src/lib/utils';

export const CHALLENGE_BUNDLE_QUERY_PARAM = 'challengeBundle';

const challengeBundleFallbackDetails = [
  {
    title: 'Challenge tracker',
  },
  {
    title: 'Recipe eBook',
  },
  {
    title: 'Private community',
  },
];

const RENEWAL_CONFIGS = {
  [ChallengeBundleParamId.SIX_MONTHS]: {
    fallbackPrice: 294,
    durationText: '6 months',
    renewText: '/6 months',
  },
  [ChallengeBundleParamId.THREE_MONTHS]: {
    fallbackPrice: 177,
    durationText: '3 months',
    renewText: '/3 months',
  },
  [ChallengeBundleParamId.EIGHT_WEEKS]: {
    fallbackPrice: 69,
    durationText: 'the challenge',
    renewText: '/month',
  },
};

const getChallengeBundleDetails = (
  localId: ChallengeBundleParamId,
  assetMetadata?: ChallengeAssetsMetadata
): ChallengePlan['details'] => {
  const items = assetMetadata?.whatsIncludedInChallenge?.[localId];
  return items?.length
    ? items.map(title => ({
        title,
      }))
    : challengeBundleFallbackDetails;
};

const getRenewalDescription = (
  matchedApiPlan: Plan | undefined | null,
  config: {
    localId: ChallengeBundleParamId;
  }
) => {
  const renewalConfig = RENEWAL_CONFIGS[config.localId];
  if (!renewalConfig) {
    return '';
  }

  const { fallbackPrice, durationText, renewText } = renewalConfig;
  const currency = getCurrencySymbol();

  const priceMetadata = matchedApiPlan?.mealPlanVariants?.[1]?.metadata;
  const metadataPrice =
    priceMetadata?.standardPlanPricingAmount ??
    matchedApiPlan?.metadata?.standardPlanPricingAmount;
  const parsedPrice = metadataPrice
    ? Number(metadataPrice.replace(/[^\d.]/g, ''))
    : NaN;

  const standardPrice =
    !isNaN(parsedPrice) && parsedPrice > 0 ? parsedPrice : fallbackPrice;
  const formattedPrice = formatPricingAmount(standardPrice);

  return `After ${durationText}, ${
    config.localId === ChallengeBundleParamId.EIGHT_WEEKS
      ? 'your subscription automatically'
      : ''
  } renews at ${currency}${formattedPrice}${renewText}. Cancel anytime.`;
};

const filterChallengeBundleConfigs = <
  T extends { localId: ChallengeBundleParamId },
>(
  configs: T[]
): T[] => {
  const searchParams = new URLSearchParams(window.location.search);
  const challengeBundleParam = searchParams.get(
    'challengeBundle'
  ) as ChallengeBundleParamId | null;
  const hideOtherPlans = searchParams.get('showall') === 'false';

  if (hideOtherPlans && challengeBundleParam) {
    if (challengeBundleParam === ChallengeBundleParamId.SIX_MONTHS) {
      return configs.filter(
        config => config.localId === ChallengeBundleParamId.SIX_MONTHS
      );
    } else if (challengeBundleParam === ChallengeBundleParamId.THREE_MONTHS) {
      return configs.filter(
        config =>
          config.localId === ChallengeBundleParamId.THREE_MONTHS ||
          config.localId === ChallengeBundleParamId.SIX_MONTHS
      );
    } else if (challengeBundleParam === ChallengeBundleParamId.EIGHT_WEEKS) {
      return configs;
    }
  }
  return configs;
};

// =====================================
// Main function to get challenge bundle plans
// =====================================
export const getChallengeBundlePlans = (
  allPlansSorted: Plan[],
  assetMetadata?: ChallengeAssetsMetadata,
  skipFilter?: boolean
): ChallengePlan[] => {
  const configs = [
    {
      localId: ChallengeBundleParamId.SIX_MONTHS,
      id: ChallengeBundleParam.SIX_MONTHS,
      price: 209,
      badgeText: 'Best Value',
      isBestValue: true,
      descriptionLine1: 'Our ultimate transformation package.',
      descriptionLine2:
        'Build real, lasting change with 6 months of personalised nutrition, accountability and support - starting with the 8 Week Challenge.',
      details: getChallengeBundleDetails(
        ChallengeBundleParamId.SIX_MONTHS,
        assetMetadata
      ),
    },
    {
      localId: ChallengeBundleParamId.THREE_MONTHS,
      id: ChallengeBundleParam.THREE_MONTHS,
      price: 149,
      descriptionLine1:
        'Go beyond the Challenge and turn momentum into lifestyle.',
      descriptionLine2:
        'Kickstart your transformation with the Spring Challenge, then continue building momentum, confidence, and long-term results well beyond spring.',
      details: getChallengeBundleDetails(
        ChallengeBundleParamId.THREE_MONTHS,
        assetMetadata
      ),
    },
    {
      localId: ChallengeBundleParamId.EIGHT_WEEKS,
      id: ChallengeBundleParam.EIGHT_WEEKS,
      price: 139,
      descriptionLine1: 'Start spring strong. Thank yourself by summer.',
      descriptionLine2:
        'Get everything you need to stay consistent, lose weight, and transform in 8 weeks.',
      details: getChallengeBundleDetails(
        ChallengeBundleParamId.EIGHT_WEEKS,
        assetMetadata
      ),
    },
  ];

  const filteredConfigs = skipFilter
    ? configs
    : filterChallengeBundleConfigs(configs);

  return filteredConfigs.map(config => {
    const matchedApiPlan = allPlansSorted.find(
      apiPlan => apiPlan.id === (config.id as unknown as string)
    );

    const { price, originalPrice } = getPlanPricing(
      matchedApiPlan,
      config.price
    );

    const plan = matchedApiPlan ?? null;

    const renewalDescription = getRenewalDescription(matchedApiPlan, config);

    return {
      localId: config.localId,
      id: config.id as unknown as string,
      price,
      originalPrice,
      badgeText: config.badgeText,
      isBestValue: config.isBestValue,
      descriptionLine1: config.descriptionLine1,
      descriptionLine2: config.descriptionLine2,
      checkoutPlanId: plan?.id || (config.id as unknown as string),
      plan,
      details: config.details,
      renewalDescription,
    };
  });
};
